import { orgAPI } from "@/api/api";
import { IntegrationEnhanced } from "@/api/integrations";
import Sentry from "@/integrations/Sentry";
import analytics from "@/utils/analytics";
import { IntegrationConnection } from "@cohesive/api-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export interface MCPOAuthMessage {
  type: "mcp-oauth-success" | "mcp-oauth-error";
  server?: string;
  error?: string;
}

export function useConnectMCP() {
  const queryClient = useQueryClient();

  const connectMCPMutation = useMutation<
    IntegrationConnection | { cancelled: boolean },
    Error,
    IntegrationEnhanced
  >({
    mutationFn: async (integration: IntegrationEnhanced) => {
      // Get the authorization URL from backend using api-client
      // Pass popup flag so backend stores it in state for callback handling
      const authUrl = await orgAPI.mcp.createConnectSession({
        integration_id: integration.id,
        popup: true,
      });

      if (!authUrl) {
        throw new Error("No authorization URL received from server");
      }

      // Open authorization URL in popup
      return new Promise((resolve, reject) => {
        const popup = window.open(
          authUrl,
          "mcp-oauth",
          "width=600,height=700,scrollbars=yes",
        );

        if (!popup) {
          reject(
            new Error("Popup blocked - please allow popups for this site"),
          );
          return;
        }

        // Listen for postMessage from popup
        const messageHandler = (event: MessageEvent<MCPOAuthMessage>) => {
          if (event.data.type === "mcp-oauth-success") {
            window.removeEventListener("message", messageHandler);
            clearInterval(timer);

            // Backend already created IntegrationConnection
            // Invalidate queries to refetch the updated connections
            queryClient.invalidateQueries({
              queryKey: ["integrationConnections"],
              refetchType: "all",
            });

            // Track the connection
            analytics.trackUserConnectionAuthorized({
              integrationId: integration.id,
              platform: integration.platform,
              provider: "mcp",
            });

            // Return a success indicator (not a full IntegrationConnection)
            resolve({ connected: true } as any);
          } else if (event.data.type === "mcp-oauth-error") {
            window.removeEventListener("message", messageHandler);
            clearInterval(timer);
            reject(new Error(event.data.error || "MCP authorization failed"));
          }
        };

        window.addEventListener("message", messageHandler);

        // Poll to check if popup was closed
        const timer = setInterval(() => {
          if (popup.closed) {
            clearInterval(timer);
            window.removeEventListener("message", messageHandler);
            resolve({ cancelled: true });
          }
        }, 500);
      });
    },
    onError: (error) => {
      Sentry.captureException(error, {
        extra: {
          provider: "mcp",
        },
      });
    },
  });

  return connectMCPMutation;
}
